import { useState } from 'react';

interface FeatureItem {
  title: string;
  description: string;
  icon: string;
  color?: string;
  details?: string;
  stat?: string;
  statLabel?: string;
}

interface FeatureGridProps {
  title: React.ReactNode;
  subtitle?: string;
  items: FeatureItem[];
  columns?: 2 | 3 | 4;
}

export default function FeatureGrid({ title, subtitle, items, columns = 3 }: FeatureGridProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const gridClass = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-2 lg:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4'
  }[columns];

  return (
    <div className="w-full">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
          {title}
        </h2>
        {subtitle && (
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>

      <div className={`grid grid-cols-1 ${gridClass} gap-8`}>
        {items.map((item, index) => (
          <div
            key={index}
            className="group bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-gray-700/50 hover:border-gray-500/50 transition-all duration-500 transform hover:scale-105 cursor-pointer h-full"
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <div className="text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse bg-gradient-to-r ${item.color || 'from-orange-500 to-red-500'}`}>
                <i className={`${item.icon} text-2xl text-white`}></i>
              </div>
              
              {item.stat && (
                <div className="mb-4">
                  <div className={`text-3xl font-bold bg-gradient-to-r ${item.color || 'from-orange-500 to-red-500'} bg-clip-text text-transparent`}>
                    {item.stat}
                  </div>
                  {item.statLabel && <div className="text-sm text-gray-400">{item.statLabel}</div>}
                </div>
              )}

              <h3 className="text-xl font-bold text-white mb-4 transition-colors duration-300">
                {item.title}
              </h3>
              
              <p className="text-gray-300 leading-relaxed mb-4">
                {item.description}
              </p>

              <div className={`transition-all duration-300 overflow-hidden ${
                hoveredIndex === index ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <p className="text-sm text-gray-400">{item.details}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}